import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { formatApiError, resolveAssetUrl } from '../../lib/api';
import { PAGE_SCHEMA } from '../../data/contentSchema';

const AdminDashboard = () => (
  <div data-testid="admin-dashboard">
    <h1 className="font-serif text-5xl text-navy">Welcome back.</h1>
    <p className="font-sans text-sm text-navy/70 mt-4 max-w-xl">
      Edit any page's copy and imagery below, or manage team members. Changes save and publish immediately.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
      {[
        { to: '/admin/content/site', title: 'Site-wide', desc: 'Header menu, footer, offices, disclaimer, copyright' },
        { to: '/admin/content/home', title: 'Home', desc: 'Hero, founders photo, pillars, commitment block' },
        { to: '/admin/content/team', title: 'Team page', desc: 'Hero, section headings, pillars' },
        { to: '/admin/content/advisory', title: 'Advisory', desc: 'Hero, Full Spectrum, Six Domains, Process' },
        { to: '/admin/content/investment', title: 'Investment', desc: 'Hero, principles, vehicles, access, disclaimer' },
        { to: '/admin/content/contact', title: 'Contact', desc: 'Intro, offices, form labels, legal text' },
        { to: '/admin/team', title: 'Team members', desc: 'Names, titles, bios, photos, emails, phones' },
      ].map((card) => (
        <a key={card.to} href={card.to} data-testid={`dashboard-card-${card.to.split('/').pop()}`} className="group border-t border-navy/20 pt-6 hover:border-navy transition-colors">
          <h3 className="font-serif text-2xl text-navy leading-tight">{card.title}</h3>
          <p className="font-sans text-sm text-navy/70 mt-3">{card.desc}</p>
          <p className="font-sans text-[10px] tracking-[0.3em] text-navy/50 mt-6 group-hover:text-navy">OPEN →</p>
        </a>
      ))}
    </div>
  </div>
);
export { AdminDashboard };

const ImageField = ({ value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr('');
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      {value && (
        <div className="relative w-48 bg-cream2 overflow-hidden border border-navy/15">
          <img src={resolveAssetUrl(value)} alt="preview" className="block w-full h-auto" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <input
          type="url" value={value || ''} onChange={(e) => onChange(e.target.value)}
          placeholder="Paste image URL or upload"
          className="flex-1 px-3 py-2 border border-navy/20 bg-cream2 text-sm font-sans text-navy focus:outline-none focus:border-navy"
        />
        <label className="font-sans text-[10px] tracking-[0.3em] px-4 py-2.5 border border-navy/30 cursor-pointer hover:bg-navy hover:text-cream transition-colors">
          {busy ? 'UPLOADING…' : 'UPLOAD'}
          <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" disabled={busy} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange('')}
            className="font-sans text-[10px] tracking-[0.3em] text-red-700 hover:underline">CLEAR</button>
        )}
      </div>
      {err && <p className="font-sans text-xs text-red-700">{err}</p>}
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 border border-navy/20 bg-cream font-serif text-navy focus:outline-none focus:border-navy';

const ListField = ({ field, value, onChange }) => {
  const update = (i, k, v) => onChange(value.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => {
    const n = [...value]; const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]]; onChange(n);
  };
  const blank = Object.fromEntries(field.itemFields.map((x) => [x.key, '']));
  const btn = 'font-sans text-[10px] tracking-[0.2em] px-2 py-1 border border-navy/20 hover:bg-navy hover:text-cream transition-colors';
  return (
    <div className="space-y-4" data-testid={`list-${field.key}`}>
      {value.map((item, i) => (
        <div key={i} className="border border-navy/15 bg-cream2 p-4" data-testid={`list-${field.key}-item-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-sans text-[10px] tracking-[0.3em] text-navy/50">ITEM {i + 1}</span>
            <div className="flex gap-2">
              <button type="button" className={btn} onClick={() => move(i, -1)} data-testid={`list-${field.key}-up-${i}`}>↑</button>
              <button type="button" className={btn} onClick={() => move(i, 1)} data-testid={`list-${field.key}-down-${i}`}>↓</button>
              <button type="button" className={`${btn} text-red-700`} onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`list-${field.key}-remove-${i}`}>REMOVE</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {field.itemFields.map((x) => (
              <div key={x.key} className={x.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="font-sans text-[10px] tracking-[0.25em] text-navy/50 block mb-1">{x.label.toUpperCase()}</label>
                {x.type === 'textarea' ? (
                  <textarea rows={3} value={item[x.key] ?? ''} onChange={(e) => update(i, x.key, e.target.value)} className={inputCls} data-testid={`list-${field.key}-${i}-${x.key}`} />
                ) : (
                  <input type="text" value={item[x.key] ?? ''} onChange={(e) => update(i, x.key, e.target.value)} className={inputCls} data-testid={`list-${field.key}-${i}-${x.key}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex gap-4">
        <button type="button" onClick={() => onChange([...value, blank])} data-testid={`list-${field.key}-add`}
          className="font-sans text-[10px] tracking-[0.3em] px-4 py-2.5 border border-navy/30 hover:bg-navy hover:text-cream transition-colors">+ ADD ITEM</button>
        <button type="button" onClick={() => onChange(field.default)} data-testid={`list-${field.key}-reset`}
          className="font-sans text-[10px] tracking-[0.2em] text-navy/40 hover:text-navy">RESET TO ORIGINAL</button>
      </div>
    </div>
  );
};

const ContentEditor = () => {
  const { page } = useParams();
  const schema = PAGE_SCHEMA[page];
  const [overrides, setOverrides] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    setLoaded(false); setStatus('');
    api.get(`/content/${page}`)
      .then((r) => setOverrides(r.data.overrides || {}))
      .catch(() => setOverrides({}))
      .finally(() => setLoaded(true));
  }, [page]);

  if (!schema) return <p className="font-sans text-navy/60">Unknown page.</p>;

  const allFields = schema.sections.flatMap((sec) => sec.fields);
  const valueOf = (fld) => (overrides[fld.key] != null ? overrides[fld.key] : fld.default);
  const setField = (k, v) => setOverrides((o) => ({ ...o, [k]: v }));

  const save = async () => {
    setSaving(true); setStatus('');
    try {
      const full = { ...overrides };
      allFields.forEach((fld) => { full[fld.key] = valueOf(fld); });
      await api.put(`/content/${page}`, { overrides: full });
      setOverrides(full);
      setStatus('Saved. Changes are live.');
    } catch (e) {
      setStatus(formatApiError(e));
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(''), 3000);
    }
  };

  return (
    <div data-testid={`content-editor-${page}`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-sans text-[11px] tracking-[0.3em] text-navy/60">EDITING</p>
          <h1 className="font-serif text-4xl text-navy mt-1">{schema.label}</h1>
        </div>
        <div className="flex items-center gap-4">
          {status && <span className="font-sans text-xs text-navy/70">{status}</span>}
          <button
            onClick={save} disabled={saving || !loaded}
            data-testid="save-content"
            className="font-sans text-[11px] tracking-[0.3em] bg-navy text-cream px-6 py-3 hover:bg-navydark disabled:opacity-60 transition-colors">
            {saving ? 'SAVING…' : 'SAVE'}
          </button>
        </div>
      </div>

      {!loaded ? (
        <p className="font-sans text-sm text-navy/60 mt-10">Loading…</p>
      ) : (
        <div className="mt-10 space-y-14">
          <p className="font-sans text-xs text-navy/60 max-w-2xl">Every box shows what is currently on the site. Edit and press SAVE to publish. Wrap words in *asterisks* for italics. A cleared box is hidden on the site.</p>
          {schema.sections.map((section) => (
            <section key={section.title} className="border-t border-navy/15 pt-8">
              <h2 className="font-serif text-2xl text-navy mb-6">{section.title}</h2>
              <div className="space-y-6">
                {section.fields.map((f) => (
                  <div key={f.key}>
                    <label className="font-sans text-[11px] tracking-[0.3em] text-navy/60 block mb-2">{f.label.toUpperCase()}</label>
                    {f.type === 'image' ? (
                      <ImageField value={valueOf(f)} onChange={(v) => setField(f.key, v)} testid={`field-${f.key}`} />
                    ) : f.type === 'list' ? (
                      <ListField field={f} value={valueOf(f) || []} onChange={(v) => setField(f.key, v)} />
                    ) : f.type === 'textarea' ? (
                      <textarea
                        value={valueOf(f) ?? ''} onChange={(e) => setField(f.key, e.target.value)}
                        rows={4}
                        data-testid={`field-${f.key}`}
                        className="w-full px-4 py-3 border border-navy/20 bg-cream2 font-serif text-navy focus:outline-none focus:border-navy"
                      />
                    ) : (
                      <input
                        type="text" value={valueOf(f) ?? ''} onChange={(e) => setField(f.key, e.target.value)}
                        data-testid={`field-${f.key}`}
                        className="w-full px-4 py-3 border border-navy/20 bg-cream2 font-serif text-navy focus:outline-none focus:border-navy"
                      />
                    )}
                    {f.type !== 'list' && f.type !== 'image' && (
                      <div className="flex gap-4 mt-1.5">
                        <button type="button" onClick={() => setField(f.key, '')} data-testid={`clear-${f.key}`}
                          className="font-sans text-[10px] tracking-[0.2em] text-navy/40 hover:text-red-700">CLEAR (HIDE ON SITE)</button>
                        <button type="button" onClick={() => setField(f.key, f.default)} data-testid={`reset-${f.key}`}
                          className="font-sans text-[10px] tracking-[0.2em] text-navy/40 hover:text-navy">RESET TO ORIGINAL</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
          <div className="pt-6 border-t border-navy/15 flex justify-end">
            <button
              onClick={save} disabled={saving || !loaded}
              data-testid="save-content-bottom"
              className="font-sans text-[11px] tracking-[0.3em] bg-navy text-cream px-6 py-3 hover:bg-navydark disabled:opacity-60 transition-colors">
              {saving ? 'SAVING…' : 'SAVE'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentEditor;
export { ImageField };
